import {
  DashboardOutlined,
  ProfileOutlined,
  UserOutlined,
  BookOutlined,
  HeartOutlined,
  DollarOutlined,
  SettingOutlined,
  ShopOutlined,
  GiftOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import type { ReactNode } from 'react';
import { PAGE_PERMS } from '@alphastep/shared-types';

/** 路由 → 页面权限点。消缺 269：菜单可见性按子栏目逐项判定 */
const PAGE_PERM_BY_PATH = new Map(PAGE_PERMS.map((p) => [p.path, p.key]));

export interface MenuRoute {
  path?: string;
  name?: string;
  icon?: ReactNode;
  routes?: MenuRoute[];
  /**
   * #225：访问该栏目所需的权限点（任一命中即可见）。
   * 不写 perms 的项对所有能进后台的账号可见（如「我的」）。
   */
  perms?: string[];
}

/** 「系统」栏目原有的分组能力点（消缺 331 前只挂在分组上） */
const SYSTEM_PERMS = ['rbac:manage', 'admin:audit:read', 'admin:user:disable'];

export const menuRoute: MenuRoute = {
  path: '/',
  routes: [
    // 消缺 332：客户预约放线下登记与两张课表；数字看板只放总览和营收。
    {
      name: '客户预约',
      perms: ['order:offline_create', 'order:zero_confirm', 'booking:any:read', 'booking:assist', 'booking:check_in', 'training_record:write', 'booking:cancel'],
      icon: <CalendarOutlined />,
      path: '/customer-booking',
      routes: [
        { path: '/ops/offline-orders/new', name: '线下资料录入', perms: ['order:offline_create'] },
        { path: '/dashboard/booking-grid-training', name: '预约课时(训练)', perms: ['booking:any:read', 'booking:assist', 'booking:check_in', 'training_record:write', 'booking:cancel'] },
        { path: '/dashboard/booking-grid-rehab', name: '预约课时(康复)', perms: ['booking:any:read', 'booking:assist', 'booking:check_in', 'booking:cancel'] },
        { path: '/ops/offline-orders/pending', name: '待确认登记', perms: ['order:offline_create', 'order:zero_confirm'] },
      ],
    },
    {
      name: '数字看板',
      perms: ['report:store:view', 'report:platform:view'],
      icon: <DashboardOutlined />,
      // 消缺 332：虚拟 key 不得与课表页的 /dashboard 前缀重叠，否则会同时展开两个分组。
      path: '/__digital-dashboard',
      routes: [
        { path: '/dashboard/overview', name: '总览' },
        { path: '/dashboard/revenue', name: '营收明细' },
      ],
    },
    // 246：栏目名由「数据订单明细」简化为「数据」
    {
      name: '数据',
      perms: ['report:store:view', 'report:platform:view', 'training_record:read'],
      icon: <ProfileOutlined />,
      path: '/data',
      routes: [
        { path: '/ops/orders', name: '订单' },
        // 252：充值明细按客户要求移出菜单——订单里已含充值单（类型=余额充值），
        // 两个入口看同一批数据，对不上时说不清该信谁。页面与路由保留于
        // /dashboard/recharge-detail，要恢复入口把这行加回即可。
        { path: '/dashboard/exchange-detail-training', name: '兑换课时(训练)' },
        { path: '/dashboard/exchange-detail-rehab', name: '兑换课时(康复)' },
        { path: '/reports/by-source', name: '来源统计', perms: ['report:platform:view'] },
        { path: '/dashboard/booking-detail-training', name: '预约统计(训练)' },
        { path: '/dashboard/booking-detail-rehab', name: '预约统计(康复)' },
        { path: '/courses/training-records', name: '训练记录' },
        { path: '/rehabs/records', name: '康复记录' },
      ],
    },
    {
      name: '会员',
      perms: ['account:read', 'account:write'],
      icon: <UserOutlined />,
      path: '/members',
      routes: [
        { path: '/members/accounts', name: '账号' },
        { path: '/members/students', name: '学员' },
      ],
    },
    // 「课程与排课」改名「训练」，与「康复」对称；预约相关整体归到预约管理下，
    // 记录类挪进数据订单明细。消缺 331：「训练」改名「闯关体适能」
    {
      name: '闯关体适能',
      perms: ['course_session:read', 'course_session:write', 'course_template:write', 'booking:any:read', 'reschedule:decide', 'store:write'],
      icon: <BookOutlined />,
      path: '/courses',
      routes: [
        { path: '/courses/templates', name: '课程模板' },
        { path: '/courses/coaches', name: '教练' },
        { path: '/courses/packages', name: '套餐' },
        { path: '/courses/sessions', name: '排课日历' },
        // 247：预约、预约台账、改约请求按客户要求移出菜单。
        // 页面与路由保留（App.tsx 仍注册），直接输 URL 可访问：
        // /courses/bookings（含代客预约入口）、/courses/booking-ledger、
        // /courses/reschedule-requests。要恢复入口把这三行加回即可。
      ],
    },
    // 消缺 331：「康复」改名「运动康复中心」
    {
      name: '运动康复中心',
      perms: ['rehab_record:write', 'course_session:read', 'store:write'],
      icon: <HeartOutlined />,
      path: '/rehabs',
      routes: [
        { path: '/rehabs/therapists', name: '康复师' },
        { path: '/rehabs/packages', name: '康复套餐' },
        // 248：康复预约按客户要求移出菜单，页面保留于 /rehabs/appointments
        { path: '/rehabs/scheduling', name: '排课日历' },
      ],
    },
    {
      name: '门店',
      perms: ['store:read', 'store:write'],
      icon: <ShopOutlined />,
      path: '/stores',
      routes: [
        { path: '/stores/list', name: '门店列表' },
        { path: '/stores/managers', name: '店长列表' },
        { path: '/stores/business', name: '业务列表' },
      ],
    },
    {
      name: '运营',
      perms: ['store:write', 'coupon:issue', 'order:refund', 'report:platform:view', 'tag:manage'],
      icon: <DollarOutlined />,
      path: '/ops',
      routes: [
        { path: '/ops/banners', name: 'Banner' },
        { path: '/ops/points', name: '积分' },
        { path: '/ops/reward-items', name: '兑换商品' },
        { path: '/ops/exchanges', name: '兑换单' },
        { path: '/ops/recharge', name: '充值管理' },
        { path: '/ops/messages', name: '消息广播' },
      ],
    },
    {
      name: '营销',
      perms: ['coupon:issue', 'store:write'],
      icon: <GiftOutlined />,
      path: '/marketing',
      routes: [
        {
          name: '优惠券',
          path: '/marketing/coupons',
          routes: [
            { path: '/ops/coupons', name: '优惠券列表' },
            { path: '/ops/coupons/issue', name: '手动发券' },
            { path: '/ops/coupons/usage', name: '优惠券使用' },
          ],
        },
        {
          name: '邀请有礼',
          path: '/marketing/invites',
          routes: [
            { path: '/marketing/invites/settings', name: '邀请设置' },
            { path: '/marketing/invites/list', name: '邀请列表' },
            { path: '/marketing/invites/draws', name: '抽奖明细' },
          ],
        },
      ],
    },
    // 消缺 331：标签管理从「数据」挪到「系统」。店长有 tag:manage 但没有系统类能力点，
    // 分组得放行 tag:manage；原有子项补上原分组的能力点，免得店长顺带看见超管账号等入口
    {
      name: '系统',
      perms: ['rbac:manage', 'admin:audit:read', 'admin:user:disable', 'tag:manage'],
      icon: <SettingOutlined />,
      path: '/system',
      routes: [
        { path: '/system/staff', name: '超管账号', perms: SYSTEM_PERMS },
        { path: '/system/admins', name: '管理员', perms: SYSTEM_PERMS },
        { path: '/system/roles', name: '身份权限', perms: SYSTEM_PERMS },
        { path: '/system/audit-logs', name: '审计日志', perms: SYSTEM_PERMS },
        { path: '/system/about', name: '关于我们', perms: SYSTEM_PERMS },
        { path: '/system/agreements', name: '协议管理', perms: SYSTEM_PERMS },
        { path: '/ops/tags', name: '标签管理', perms: ['tag:manage'] },
        { path: '/system/me', name: '我的' },
      ],
    },
  ],
};

/**
 * #225：按当前账号的有效权限裁剪菜单。
 * - 内置 admin（isAdmin）看全部，避免身份配置漏项把管理员锁在外面
 * - 项上没写 perms 的默认可见（如「我的」）
 * - 分组的任一权限命中即保留该分组；分组全无子项时也一并隐藏
 */
export function filterMenuByPerms(
  route: MenuRoute,
  perms: string[],
  isAdmin: boolean,
): MenuRoute {
  if (isAdmin) return route;
  const owned = new Set(perms);
  // 消缺 269：自定义身份现在配的是「页面权限点」（page:xxx），一个子栏目一个。
  // 只要身份里带了页面点，就以页面点为准判可见性；内置角色（admin/manager…）
  // 不带页面点，仍走原来的能力点判断——否则这次改动会让现有账号突然看不到菜单。
  const hasPagePerms = perms.some((p) => p.startsWith('page:'));
  const pageKeyOf = (path?: string) => PAGE_PERM_BY_PATH.get(path ?? '');

  const visible = (item: MenuRoute): boolean => {
    if (hasPagePerms) {
      const key = pageKeyOf(item.path);
      // 叶子节点（有对应页面点的）按页面点判；分组节点由它的子项决定，这里先放行
      if (key) return owned.has(key);
      if (!item.routes) return !item.perms || item.perms.some((p) => owned.has(p));
      return true;
    }
    return !item.perms || item.perms.some((p) => owned.has(p));
  };

  const walk = (item: MenuRoute): MenuRoute | null => {
    if (!visible(item)) return null;
    if (!item.routes) return item;
    const kids = item.routes.map(walk).filter((x): x is MenuRoute => x !== null);
    // 页面点模式下，子项被裁空的分组直接隐藏——留一个点进去全是空的栏目更费解
    if (kids.length === 0 && (hasPagePerms || !item.path)) return null;
    return { ...item, routes: kids };
  };

  return walk(route) ?? { path: '/', routes: [] };
}

/** 老路径 → 新路径，路由层做 <Navigate> */
export const OLD_REDIRECTS: Record<string, string> = {
  '/': '/dashboard/overview',
  '/stores': '/system/stores',
  '/accounts': '/members/accounts',
  '/packages': '/courses/packages',
  '/orders': '/ops/orders',
  '/messages': '/ops/messages',
  '/audit-logs': '/system/audit-logs',
  '/reschedule-requests': '/courses/reschedule-requests',
  '/coupons/issue': '/ops/coupons/issue',
  '/system/stores': '/stores/list',
  '/ops/invites': '/marketing/invites/list',
};
